<template>
    <glass-dialog :value="value" title="Ativar publicação automática" :max-width="640" persistent @input="$emit('input', $event)">
        <eyebrow primary :text="`Passo ${step} de 3`" class="mb-3" />

        <!-- Passo 1: frequência -->
        <div v-if="step === 1">
            <p class="body-sm mb-1 ma-0">Quantos artigos você quer publicar por mês?</p>
            <p class="hint-info mb-4 ma-0">Para começar, 2 por mês já faz diferença. Você pode mudar depois.</p>
            <div class="freq-grid">
                <button
                    v-for="opt in frequencyOptions"
                    :key="opt.value"
                    type="button"
                    class="freq-card"
                    :class="{ 'freq-card--active': frequency === opt.value }"
                    @click="setFrequency(opt.value)"
                >
                    <v-icon :color="frequency === opt.value ? 'primary' : 'mercury'" size="30">{{ opt.icon }}</v-icon>
                    <span class="freq-title">{{ opt.title }}</span>
                    <span class="freq-hint">{{ opt.hint }}</span>
                </button>
            </div>
        </div>

        <!-- Passo 2: dias + horários -->
        <div v-else-if="step === 2">
            <p class="body-sm mb-1 ma-0">
                {{ requiredDays === 1
                    ? 'Escolha o dia da semana e o horário. O artigo será publicado a cada 2 semanas.'
                    : 'Escolha os 2 dias da semana e o horário de publicação.' }}
            </p>
            <p class="hint-info mb-3 ma-0">Dica: horários da manhã costumam funcionar bem.</p>
            <div class="weekday-row mb-4">
                <button
                    v-for="d in weekdays"
                    :key="d.value"
                    type="button"
                    class="weekday-chip"
                    :class="{ 'weekday-chip--active': isDaySelected(d.value) }"
                    @click="toggleDay(d.value)"
                >
                    {{ d.label }}
                </button>
            </div>

            <div v-for="slot in selectedDays" :key="slot.weekday" class="slot-time-row">
                <div class="slot-day">
                    <v-icon small color="primary" class="mr-2">mdi-calendar-week</v-icon>
                    {{ weekdayLabel(slot.weekday) }}
                </div>
                <v-menu :close-on-content-click="false" offset-y min-width="290">
                    <template #activator="{ on, attrs }">
                        <v-text-field
                            :value="slot.time"
                            label="Horário"
                            outlined dense readonly hide-details
                            prepend-inner-icon="mdi-clock-outline"
                            style="max-width: 150px"
                            v-bind="attrs" v-on="on"
                        />
                    </template>
                    <v-time-picker
                        :value="slot.time"
                        format="24hr"
                        :allowed-minutes="allowedMinutes"
                        @input="setSlotTime(slot, $event)"
                    />
                </v-menu>
            </div>

            <p v-if="selectedDays.length < requiredDays" class="hint-warn mt-2 ma-0">
                Selecione {{ requiredDays }} {{ requiredDays === 1 ? 'dia' : 'dias' }} da semana.
            </p>
        </div>

        <!-- Passo 3: preview -->
        <div v-else-if="step === 3">
            <p class="body-sm mb-1 ma-0">
                Distribuímos {{ orderedThemes.length }} tema{{ orderedThemes.length > 1 ? 's' : '' }} nas próximas datas.
                Reordene ou remova antes de confirmar.
            </p>
            <info-notice
                variant="primary"
                icon="mdi-email-check-outline"
                class="mb-3 mt-2"
                description="Como funciona: escrevemos cada artigo 2 dias antes da data e enviamos por e-mail para você aprovar. Cada artigo usa 1 crédito."
            />
            <p v-if="creditShortage > 0" class="hint-warn mb-3 ma-0">
                <v-icon x-small color="error" class="mr-1">mdi-alert-outline</v-icon>
                Você tem {{ credits }} crédito{{ credits === 1 ? '' : 's' }} e este planejamento usa {{ distribution.length }}.
                Os artigos além do saldo só serão gerados quando você recarregar créditos.
            </p>

            <div class="preview-list">
                <div v-for="(row, i) in distribution" :key="row.theme._id" class="preview-row">
                    <div class="preview-date">
                        <div class="preview-date-day">{{ formatDay(row.date) }}</div>
                        <div class="preview-date-time">{{ formatTime(row.date) }}</div>
                    </div>
                    <div class="preview-theme">{{ row.theme.title || row.theme.theme }}</div>
                    <div class="preview-actions">
                        <v-btn icon x-small :disabled="i === 0" @click="move(i, -1)"><v-icon small>mdi-arrow-up</v-icon></v-btn>
                        <v-btn icon x-small :disabled="i === distribution.length - 1" @click="move(i, 1)"><v-icon small>mdi-arrow-down</v-icon></v-btn>
                        <v-btn icon x-small color="error" @click="removeTheme(row.theme)"><v-icon small>mdi-close</v-icon></v-btn>
                    </div>
                </div>
            </div>

            <div v-if="removedThemes.length" class="removed-pool mt-3">
                <div class="removed-title">Temas removidos</div>
                <div class="removed-chips">
                    <button v-for="t in removedThemes" :key="t._id" type="button" class="removed-chip" @click="addBack(t)">
                        <v-icon x-small class="mr-1">mdi-plus</v-icon>{{ t.title || t.theme }}
                    </button>
                </div>
            </div>

            <empty-state
                v-if="orderedThemes.length === 0"
                icon="mdi-calendar-remove-outline"
                title="Nenhum tema para agendar."
                class="mt-4"
            />
        </div>

        <template #actions>
            <v-btn text :disabled="submitting" @click="back">{{ step === 1 ? 'Cancelar' : 'Voltar' }}</v-btn>
            <v-spacer />
            <v-btn
                v-if="step < 3"
                color="primary"
                :disabled="!canAdvance"
                @click="next"
            >Continuar</v-btn>
            <v-btn
                v-else
                color="primary"
                :loading="submitting"
                :disabled="orderedThemes.length === 0"
                @click="confirm"
            >
                <v-icon left>mdi-calendar-check</v-icon>
                Ativar — agendar {{ orderedThemes.length }} artigo{{ orderedThemes.length > 1 ? 's' : '' }}
            </v-btn>
        </template>
    </glass-dialog>
</template>

<script>
import { format, addDays, startOfDay } from 'date-fns'
import Api from '@/lib/Api'
import { emitToastr } from '@/lib/Utils'

const SAO_PAULO_UTC_OFFSET = '-03:00'

export default {
    name: 'ArtigosPlanWizard',
    props: {
        value: { type: Boolean, default: false },
        plannableThemes: { type: Array, default: () => [] },
    },
    data() {
        return {
            step: 1,
            frequency: 2, // artigos por mês (2 = quinzenal, 8 = 2x por semana)
            frequencyOptions: [
                { value: 2, icon: 'mdi-numeric-2-circle-outline', title: '2 artigos / mês', hint: 'Um post a cada 2 semanas' },
                { value: 8, icon: 'mdi-numeric-8-circle-outline', title: '8 artigos / mês', hint: 'Dois posts por semana' },
            ],
            selectedDays: [], // [{ weekday, time }]
            orderedThemes: [],
            removedThemes: [],
            submitting: false,
            weekdays: [
                { value: 0, label: 'Dom' },
                { value: 1, label: 'Seg' },
                { value: 2, label: 'Ter' },
                { value: 3, label: 'Qua' },
                { value: 4, label: 'Qui' },
                { value: 5, label: 'Sex' },
                { value: 6, label: 'Sáb' },
            ],
        }
    },
    computed: {
        // Quantos dias da semana o usuário precisa escolher no passo 2.
        requiredDays() {
            return this.frequency === 2 ? 1 : 2
        },
        // Intervalo entre publicações de um mesmo slot: quinzenal (2/mês) ou semanal (8/mês).
        cadenceDays() {
            return this.frequency === 2 ? 14 : 7
        },
        canAdvance() {
            if (this.step === 1) return !!this.frequency
            if (this.step === 2) return this.selectedDays.length === this.requiredDays
            return true
        },
        credits() {
            return this.$store?.state?.currentUser?.articles_quantity ?? null
        },
        isAdmin() {
            return this.$store?.state?.currentUser?.role === 'admin'
        },
        // Quantos artigos do plano ficarão sem crédito (admin é isento).
        creditShortage() {
            if (this.isAdmin || typeof this.credits !== 'number') return 0
            return Math.max(0, this.distribution.length - this.credits)
        },
        // Distribui os temas ordenados nas próximas datas dos slots, respeitando
        // a cadência do plano (a cada 14 dias no 2/mês, semanal no 8/mês).
        distribution() {
            const slots = [...this.selectedDays].sort((a, b) => a.weekday - b.weekday)
            if (!slots.length || !this.orderedThemes.length) return []

            const count = this.orderedThemes.length
            const dates = []
            for (const slot of slots) {
                const [hh, mm] = (slot.time || '09:00').split(':').map(Number)
                let day = startOfDay(addDays(new Date(), 1)) // começa amanhã
                while (day.getDay() !== slot.weekday) day = addDays(day, 1)
                for (let i = 0; i < count; i++) {
                    dates.push(new Date(day.getFullYear(), day.getMonth(), day.getDate(), hh, mm, 0, 0))
                    day = addDays(day, this.cadenceDays)
                }
            }
            dates.sort((a, b) => a - b)
            return dates.slice(0, count).map((date, i) => ({ theme: this.orderedThemes[i], date }))
        },
    },
    methods: {
        reset() {
            this.step = 1
            this.frequency = 2
            this.selectedDays = []
            this.orderedThemes = [...this.plannableThemes]
            this.removedThemes = []
            this.submitting = false
        },
        setFrequency(opt) {
            this.frequency = opt
            if (this.selectedDays.length > this.requiredDays) this.selectedDays = this.selectedDays.slice(0, this.requiredDays)
        },
        allowedMinutes(m) {
            return m % 5 === 0
        },
        isDaySelected(wd) {
            return this.selectedDays.some(s => s.weekday === wd)
        },
        weekdayLabel(wd) {
            const map = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado']
            return map[wd]
        },
        toggleDay(wd) {
            const existing = this.selectedDays.findIndex(s => s.weekday === wd)
            if (existing !== -1) {
                this.selectedDays.splice(existing, 1)
                return
            }
            if (this.selectedDays.length >= this.requiredDays) {
                emitToastr('info', `Você pode selecionar apenas ${this.requiredDays} dia${this.requiredDays > 1 ? 's' : ''}.`)
                return
            }
            this.selectedDays.push({ weekday: wd, time: '09:00' })
            this.selectedDays.sort((a, b) => a.weekday - b.weekday)
        },
        setSlotTime(slot, time) {
            slot.time = time
        },
        move(i, dir) {
            const j = i + dir
            if (j < 0 || j >= this.orderedThemes.length) return
            // distribution index corresponde a orderedThemes index
            const arr = this.orderedThemes
            const tmp = arr[i]
            this.$set(arr, i, arr[j])
            this.$set(arr, j, tmp)
        },
        removeTheme(theme) {
            const idx = this.orderedThemes.findIndex(t => t._id === theme._id)
            if (idx !== -1) {
                this.orderedThemes.splice(idx, 1)
                this.removedThemes.push(theme)
            }
        },
        addBack(theme) {
            const idx = this.removedThemes.findIndex(t => t._id === theme._id)
            if (idx !== -1) {
                this.removedThemes.splice(idx, 1)
                this.orderedThemes.push(theme)
            }
        },
        formatDay(date) {
            return format(date, 'dd/MM')
        },
        formatTime(date) {
            return format(date, 'HH:mm')
        },
        datePayload(date) {
            return `${format(date, 'yyyy-MM-dd')}T${format(date, 'HH:mm')}:00${SAO_PAULO_UTC_OFFSET}`
        },
        back() {
            if (this.step === 1) {
                this.$emit('input', false)
            } else {
                this.step -= 1
            }
        },
        next() {
            if (!this.canAdvance) return
            this.step += 1
        },
        async confirm() {
            if (this.submitting || this.orderedThemes.length === 0) return
            this.submitting = true
            try {
                const items = this.distribution.map(row => ({
                    theme_id: row.theme._id,
                    scheduled_date: this.datePayload(row.date),
                }))

                const resp = await Api.createPlan({
                    frequency: this.frequency,
                    slots: this.selectedDays.map(s => ({ weekday: s.weekday, time: s.time })),
                    items,
                })

                if (resp?.error) {
                    emitToastr('error', resp.message || 'Erro ao criar planejamento.')
                    return
                }

                emitToastr('success', 'Pronto! Publicação automática ativada.', 'Você receberá um e-mail antes de cada publicação.')
                this.$emit('created')
                this.$emit('input', false)
            } catch (err) {
                emitToastr('error', err?.message || 'Erro ao criar planejamento.')
            } finally {
                this.submitting = false
            }
        },
    },
    watch: {
        value(val) {
            if (val) this.reset()
        },
    },
}
</script>

<style scoped>
.freq-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.freq-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 22px 16px;
    border-radius: var(--radius-sm);
    border: 1.5px solid var(--glass-panel-border);
    background: var(--glass-soft-bg);
    cursor: pointer;
    transition: all 160ms ease;
    font-family: inherit;
}

.freq-card--active {
    border-color: var(--color-primary);
    background: rgba(112, 42, 225, 0.08);
}

.freq-title {
    font-weight: 700;
    font-size: 15px;
    color: var(--color-on-surface);
}

.freq-hint {
    font-size: 12px;
    color: var(--color-on-surface-variant);
}

.weekday-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.weekday-chip {
    min-width: 52px;
    padding: 9px 0;
    border-radius: var(--radius-pill);
    border: 1px solid var(--glass-panel-border);
    background: var(--glass-soft-bg);
    color: var(--color-on-surface-variant);
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    transition: all 140ms ease;
    font-family: inherit;
}

.weekday-chip--active {
    border-color: var(--color-primary);
    background: rgba(112, 42, 225, 0.12);
    color: var(--color-primary);
}

.slot-time-row {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 10px 0;
    border-top: 1px solid var(--glass-panel-border);
}

.slot-day {
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: 14px;
    color: var(--color-on-surface);
    min-width: 130px;
}

.preview-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 46vh;
    overflow-y: auto;
}

.preview-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 10px;
    border-radius: var(--radius-xs);
    border: 1px solid var(--glass-panel-border);
    background: var(--glass-soft-bg);
}

.preview-date {
    text-align: center;
    min-width: 52px;
}

.preview-date-day {
    font-weight: 800;
    font-size: 14px;
    color: var(--color-primary);
    line-height: 1.1;
}

.preview-date-time {
    font-size: 11px;
    color: var(--color-on-surface-variant);
}

.preview-theme {
    flex: 1;
    font-size: 13.5px;
    color: var(--color-on-surface);
    line-height: 1.3;
}

.preview-actions {
    display: flex;
    align-items: center;
    gap: 2px;
}

.removed-pool {
    border-top: 1px dashed var(--glass-panel-border);
    padding-top: 10px;
}

.removed-title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-on-surface-variant);
    margin-bottom: 6px;
}

.removed-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.removed-chip {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: var(--radius-pill);
    border: 1px dashed var(--glass-panel-border);
    background: transparent;
    color: var(--color-on-surface-variant);
    font-size: 12.5px;
    cursor: pointer;
    font-family: inherit;
}

.removed-chip:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
}

.hint-warn {
    font-size: 13px;
    color: var(--color-error, #b41340);
}

.hint-info {
    font-size: 12.5px;
    color: var(--color-on-surface-variant);
}
</style>
